import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface FleetDecisions {
  rejected: string[];
  accepted: string[];
}

interface RecommendationDecisionsState {
  byFleet: Record<string, FleetDecisions>;
  reject: (fleetId: string, recommendationId: string) => void;
  accept: (fleetId: string, recommendationId: string) => void;
  reset: (fleetId: string) => void;
}

const ensureFleet = (state: RecommendationDecisionsState, fleetId: string): FleetDecisions => state.byFleet[fleetId] ?? { rejected: [], accepted: [] };

const addUnique = (list: string[], id: string): string[] => (list.includes(id) ? list : [...list, id]);

// Persists per-fleet decisions so accepted/rejected POI suggestions don't reappear
// after a workflow refetch. Per-browser only — no backend round-trip yet.
const useRecommendationDecisions = create<RecommendationDecisionsState>()(
  persist(
    (set) => ({
      byFleet: {},
      reject: (fleetId, recommendationId) =>
        set((state) => {
          const current = ensureFleet(state, fleetId);
          return {
            byFleet: {
              ...state.byFleet,
              [fleetId]: { rejected: addUnique(current.rejected, recommendationId), accepted: current.accepted },
            },
          };
        }),
      accept: (fleetId, recommendationId) =>
        set((state) => {
          const current = ensureFleet(state, fleetId);
          return {
            byFleet: {
              ...state.byFleet,
              [fleetId]: { rejected: current.rejected, accepted: addUnique(current.accepted, recommendationId) },
            },
          };
        }),
      reset: (fleetId) =>
        set((state) => {
          if (!state.byFleet[fleetId]) return state;
          const next = { ...state.byFleet };
          delete next[fleetId];
          return { byFleet: next };
        }),
    }),
    {
      name: 'geofence-recommendation-decisions',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    }
  )
);

export default useRecommendationDecisions;
